// Axios instance with auth interceptor

import axios, { type InternalAxiosRequestConfig } from 'axios'

const API_URL = import.meta.env.VITE_API_URL || ''

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

type PersistedAuthState = {
  token?: string | null
  refreshToken?: string | null
  user?: unknown
}

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean
}

const AUTH_STORAGE_KEY = 'cti-auth'

const getPersistedAuthState = (): PersistedAuthState => {
  const raw = localStorage.getItem(AUTH_STORAGE_KEY)
  if (!raw) return {}

  try {
    return JSON.parse(raw) as PersistedAuthState
  } catch {
    return {}
  }
}

const persistAuthState = (state: PersistedAuthState) => {
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(state))
}

export const getAccessToken = () =>
  localStorage.getItem('token') || getPersistedAuthState().token || null

export const getRefreshToken = () => getPersistedAuthState().refreshToken || null

export const updateStoredTokens = (accessToken: string, refreshToken: string) => {
  const state = getPersistedAuthState()
  localStorage.setItem('token', accessToken)
  persistAuthState({
    ...state,
    token: accessToken,
    refreshToken,
  })
}

const clearStoredAuth = () => {
  localStorage.removeItem('token')
  localStorage.removeItem(AUTH_STORAGE_KEY)
}

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = getAccessToken()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

// Refresh access tokens on 401 before forcing logout.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as RetryableRequestConfig | undefined
    const status = error.response?.status
    const url = originalRequest?.url || ''
    const isAuthRequest = url.includes('/api/v1/auth/login') || url.includes('/api/v1/auth/refresh')

    if (status === 401 && originalRequest && !originalRequest._retry && !isAuthRequest) {
      const refreshToken = getRefreshToken()

      if (refreshToken) {
        originalRequest._retry = true

        try {
          const response = await axios.post(`${API_URL}/api/v1/auth/refresh`, null, {
            headers: {
              Authorization: `Bearer ${refreshToken}`,
            },
          })

          updateStoredTokens(response.data.access_token, response.data.refresh_token)
          originalRequest.headers.Authorization = `Bearer ${response.data.access_token}`
          return api(originalRequest)
        } catch {
          clearStoredAuth()
        }
      } else {
        clearStoredAuth()
      }
    }

    if (status === 401) {
      clearStoredAuth()

      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login'
      }
    }

    return Promise.reject(error)
  }
)

export default api
